import { useEffect, useRef, useState } from "react";
import { Badge, Empty, PageTitle, errorMessage } from "./components";
import { PlaceFilter } from "./Discovery";
import {
  engagementStatus,
  pilotApi,
  pilotTime,
  pendingReservationCommand,
} from "./place-pilot";
import type {
  PilotCatalog,
  PilotEngagements,
  PilotEvent,
  PilotReservation,
} from "./place-pilot-types";
import type { ReservationDraft } from "./view-models";
import { venueSlot } from "./place-pilot-time";
import { ApiError } from "./api";
type Actions = {
  signedIn: boolean;
  onAuth: () => void;
  onRefresh: () => Promise<void>;
};
export function Engagements({
  catalog,
  engagements,
  onEvent,
  onReservation,
  upcoming = false,
}: {
  catalog: PilotCatalog;
  engagements: PilotEngagements;
  onEvent: (id: string) => void;
  onReservation: (id: string) => void;
  upcoming?: boolean;
}) {
  const registrations = engagements.registrations.filter(
    (r) =>
      !upcoming ||
      (r.status === "confirmed" &&
        new Date(r.event.ends_at).valueOf() > Date.now()),
  );
  const reservations = engagements.reservations.filter(
    (r) =>
      !upcoming ||
      (["pending", "confirmed"].includes(r.status) &&
        new Date(r.ends_at).valueOf() > Date.now()),
  );
  const items = [
    ...registrations.map((r) => ({
      id: r.id,
      title: r.event.title,
      starts_at: r.event.starts_at,
      timezone: r.event.timezone,
      place_id: r.event.place_id,
      label:
        r.event.status === "cancelled"
          ? "Activité annulée"
          : r.status === "confirmed"
            ? "Inscription confirmée"
            : "Inscription annulée",
      open: () => onEvent(r.event_id),
    })),
    ...reservations.map((r) => ({
      id: r.id,
      title: r.space.name,
      starts_at: r.starts_at,
      timezone: r.space.timezone,
      place_id: r.place_id,
      label: engagementStatus[r.status],
      open: () => onReservation(r.id),
    })),
  ].sort(
    (a, b) =>
      a.starts_at.localeCompare(b.starts_at) || a.id.localeCompare(b.id),
  );
  const visible = upcoming ? items.slice(0, 6) : items;
  return (
    <div className="stack">
      {visible.map((item) => (
        <button
          key={item.id}
          className="panel discovery-row"
          onClick={item.open}
        >
          <span>
            <strong>{item.title}</strong>
            <span>
              {pilotTime(item.starts_at, item.timezone)} ·{" "}
              {catalog.places.find((p) => p.id === item.place_id)?.name}
            </span>
          </span>
          <Badge>{item.label}</Badge>
        </button>
      ))}
      {!visible.length && (
        <Empty title="Aucun engagement à venir.">
          Découvrez la programmation ou demandez un espace.
        </Empty>
      )}
      {visible.length < items.length && (
        <p className="fineprint">
          Retrouvez tous vos engagements dans Mon agenda et Mes réservations.
        </p>
      )}
    </div>
  );
}
function EventFacts({ event }: { event: PilotEvent }) {
  return (
    <>
      <p>
        {pilotTime(event.starts_at, event.timezone)} →{" "}
        {pilotTime(event.ends_at, event.timezone)} · {event.timezone}
      </p>
      <p>
        {event.pricing?.state === "free"
          ? "Activité gratuite"
          : event.pricing?.state === "paid"
            ? "Activité payante · inscription non ouverte ici"
            : "Conditions commerciales à préciser"}{" "}
        · {event.pricing?.terms}
      </p>
      <p>
        {event.remaining_capacity} place(s) restante(s) sur {event.capacity}
      </p>
      {event.synthetic && (
        <p className="note">Données de recette synthétiques.</p>
      )}
    </>
  );
}
export function OperationalAgenda({
  catalog,
  engagements,
  placeId,
  onPlaceChange,
  eventId,
  onEvent,
  signedIn,
  onAuth,
  onRefresh,
}: Actions & {
  catalog: PilotCatalog;
  engagements: PilotEngagements;
  placeId: string;
  onPlaceChange: (id: string) => void;
  eventId: string | null;
  onEvent: (id: string | null) => void;
}) {
  const [mine, setMine] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const keys = useRef<Record<string, string>>({});
  const event =
    catalog.events.find((e) => e.id === eventId) ||
    engagements.registrations.find((r) => r.event_id === eventId)?.event;
  const registration = engagements.registrations.find(
    (r) => r.event_id === eventId && r.status === "confirmed",
  );
  async function action() {
    if (!signedIn) {
      onAuth();
      return;
    }
    if (!event) return;
    const command = registration
      ? `cancel:${registration.id}:${registration.revision}`
      : `register:${event.id}:${engagements.registrations.find((r) => r.event_id === event.id)?.revision ?? 0}`;
    const key = (keys.current[command] ||= crypto.randomUUID());
    setBusy(true);
    setMessage("");
    try {
      if (registration) await pilotApi.cancelRegistration(registration.id, key);
      else await pilotApi.register(event.id, key);
      delete keys.current[command];
      await onRefresh();
      setMessage(
        registration
          ? "Votre inscription est annulée."
          : "Votre inscription est confirmée.",
      );
    } catch (e) {
      setMessage(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageTitle eyebrow="Se retrouver" title={event?.title || "Agenda"}>
        La programmation de tous les lieux et vos participations.
      </PageTitle>
      {message && (
        <p role="status" className="notice">
          {message}
        </p>
      )}
      {event ? (
        <>
          <button className="text-button" onClick={() => onEvent(null)}>
            ← Retour à l’agenda
          </button>
          <section className="panel">
            <Badge>
              {event.status === "cancelled"
                ? "Activité annulée"
                : registration
                  ? "Inscription confirmée"
                  : event.registration_state === "full"
                    ? "Complet"
                    : event.registration_state === "open"
                      ? "Inscriptions ouvertes"
                      : "Inscriptions fermées"}
            </Badge>
            <p>{event.description}</p>
            <EventFacts event={event} />
            <p>{catalog.places.find((p) => p.id === event.place_id)?.name}</p>
            <p className="fineprint">
              Votre inscription ne publie pas votre identité et ne déclare
              aucune présence.
            </p>
            <button
              className="button primary"
              disabled={
                busy ||
                (!registration &&
                  (event.registration_state !== "open" ||
                    event.pricing.state !== "free" ||
                    event.source.type !== "native"))
              }
              onClick={() => void action()}
            >
              {registration
                ? "Annuler mon inscription"
                : event.source.type !== "native"
                  ? "Inscriptions auprès de l’organisateur"
                  : event.pricing.state !== "free"
                    ? "Conditions d’inscription à établir"
                    : signedIn
                      ? "M’inscrire gratuitement"
                      : "Créer mon compte pour participer"}
            </button>
          </section>
        </>
      ) : (
        <>
          <div className="segments">
            <button aria-pressed={!mine} onClick={() => setMine(false)}>
              Programmation
            </button>
            <button aria-pressed={mine} onClick={() => setMine(true)}>
              Mon agenda
            </button>
          </div>
          {mine ? (
            <>
              {!signedIn ? (
                <button className="button" onClick={onAuth}>
                  Me connecter pour retrouver mes inscriptions
                </button>
              ) : (
                <>
                  <p className="note">
                    Vos inscriptions restent visibles pour tous les lieux.
                  </p>
                  {engagements.registrations.map((r) => (
                    <button
                      className="panel discovery-row"
                      key={r.id}
                      onClick={() => onEvent(r.event_id)}
                    >
                      <span>
                        <strong>{r.event.title}</strong>
                        <span>
                          {pilotTime(r.event.starts_at, r.event.timezone)}
                        </span>
                      </span>
                      <Badge>
                        {r.event.status === "cancelled"
                          ? "Activité annulée"
                          : engagementStatus[r.status]}
                      </Badge>
                    </button>
                  ))}
                  {!engagements.registrations.length && (
                    <Empty title="Aucune inscription pour le moment." />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <PlaceFilter
                catalog={catalog}
                placeId={placeId}
                onChange={onPlaceChange}
              />
              <div className="stack">
                {catalog.events
                  .filter((e) => !placeId || e.place_id === placeId)
                  .map((e) => (
                    <article className="panel" key={e.id} data-event-id={e.id}>
                      <h2>{e.title}</h2>
                      <EventFacts event={e} />
                      <button className="button" onClick={() => onEvent(e.id)}>
                        Voir l’activité
                      </button>
                    </article>
                  ))}
              </div>
              {!catalog.events.some(
                (e) => !placeId || e.place_id === placeId,
              ) && <Empty title="La programmation se prépare." />}
            </>
          )}
        </>
      )}
    </>
  );
}
export function OperationalReserve({
  catalog,
  engagements,
  placeId,
  onPlaceChange,
  draft,
  onDraft,
  signedIn,
  onAuth,
  onRefresh,
  reservationId,
  onReservation,
}: Actions & {
  catalog: PilotCatalog;
  engagements: PilotEngagements;
  placeId: string;
  onPlaceChange: (id: string) => void;
  draft: ReservationDraft;
  onDraft: (d: ReservationDraft) => void;
  reservationId: string | null;
  onReservation: (id: string | null) => void;
}) {
  const [mine, setMine] = useState(false),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const key = useRef<{
    body: string;
    key: string;
  } | null>(null);
  const selected = catalog.spaces.find((s) => s.id === draft.space_id);
  const [acceptedConditions, setAcceptedConditions] = useState<{
    space_id: string;
    version: string;
  } | null>(null);
  const [conditionsReloadRequired, setConditionsReloadRequired] =
    useState(false);
  const conditionsAvailable = /^sha256:[a-f0-9]{64}$/.test(
    selected?.conditions_version || "",
  );
  const conditionsAccepted =
    !!selected &&
    conditionsAvailable &&
    acceptedConditions?.space_id === selected.id &&
    acceptedConditions.version === selected.conditions_version;
  const pendingCommand = pendingReservationCommand(draft);
  const reservation = engagements.reservations.find(
    (r) => r.id === reservationId,
  );
  async function submit(replay = false) {
    const previous = replay ? pendingCommand : null;
    if (replay && !previous) return;
    if (!previous && !selected) return;
    if (!signedIn) {
      onAuth();
      return;
    }
    if (
      !previous &&
      (pendingCommand ||
        conditionsReloadRequired ||
        !conditionsAccepted ||
        !acceptedConditions)
    ) {
      setMessage(
        "Consultez et validez les conditions affichées avant d’envoyer votre demande.",
      );
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const body =
        previous?.body ??
        (() => ({
          space_id: selected!.id,
          ...venueSlot(
            draft.date,
            draft.time,
            draft.duration,
            selected!.timezone,
          ),
          purpose: draft.purpose,
          conditions_version: acceptedConditions!.version,
        }))();
      const fingerprint = JSON.stringify(body);
      if (previous) key.current = { body: fingerprint, key: previous.key };
      else if (key.current?.body !== fingerprint) {
        key.current = {
          body: fingerprint,
          key:
            draft.request_fingerprint === fingerprint && draft.request_key
              ? draft.request_key
              : crypto.randomUUID(),
        };
        onDraft({
          ...draft,
          request_key: key.current.key,
          request_fingerprint: fingerprint,
        });
      }
      const result = await pilotApi.reserve(body, key.current.key);
      await onRefresh();
      if (!mounted.current) return;
      key.current = null;
      onDraft({
        ...draft,
        request_key: undefined,
        request_fingerprint: undefined,
      });
      onReservation(result.id);
      setMine(true);
      setMessage("Demande envoyée. Elle attend la décision de l’équipe.");
    } catch (e) {
      if (!mounted.current) return;
      if (e instanceof ApiError && e.code === "conditions_changed") {
        setAcceptedConditions(null);
        setConditionsReloadRequired(true);
        key.current = null;
        onDraft({
          ...draft,
          request_key: undefined,
          request_fingerprint: undefined,
        });
        try {
          await onRefresh();
          if (mounted.current) {
            setConditionsReloadRequired(false);
            setMessage(
              `${e.message} Votre brouillon est conservé. Relisez les nouvelles conditions et validez-les avant de renvoyer votre demande.`,
            );
          }
        } catch {
          if (mounted.current)
            setMessage(
              `${e.message} Votre brouillon est conservé, mais les nouvelles conditions ne peuvent pas encore être chargées. Actualisez la page avant de les valider.`,
            );
        }
      } else {
        if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
          key.current = null;
          onDraft({
            ...draft,
            request_key: undefined,
            request_fingerprint: undefined,
          });
        }
        setMessage(errorMessage(e));
      }
    } finally {
      if (mounted.current) setBusy(false);
    }
  }
  async function cancel(r: PilotReservation) {
    setBusy(true);
    setMessage("");
    try {
      await pilotApi.cancelReservation(r.id, `cancel-reservation-${r.id}`);
      await onRefresh();
      setMessage("Votre annulation est enregistrée.");
    } catch (e) {
      setMessage(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  function booking(r: PilotReservation) {
    const accepted = r.conditions_snapshot;
    const timezone = accepted ? accepted.timezone : r.space.timezone;
    const terms = accepted
      ? accepted.commercial_terms
      : r.space.commercial_terms;
    const changed =
      accepted &&
      Object.entries(accepted).some(
        ([key, value]) =>
          (key === "conditions"
            ? r.space.conditions
            : r.space[key as keyof typeof r.space]) !== value,
      );
    return (
      <article className="panel" key={r.id}>
        <h2>{r.space.name}</h2>
        <Badge>{engagementStatus[r.status]}</Badge>
        <p>
          {pilotTime(r.starts_at, timezone)} → {pilotTime(r.ends_at, timezone)}{" "}
          · {timezone}
        </p>
        <p>{r.purpose}</p>
        {r.decision_reason && (
          <p className="note">Réponse de l’équipe : {r.decision_reason}</p>
        )}
        <p>
          {terms ||
            "Conditions commerciales non établies lors de cette demande. À préciser avec l’équipe."}
        </p>
        {r.status === "pending" && changed && (
          <p className="note">
            Les conditions ont changé depuis votre demande. Relisez les
            conditions de l’espace puis annulez et déposez une nouvelle demande
            pour les accepter.
          </p>
        )}
        {r.status === "pending" && (
          <p className="note">
            Cette demande ne bloque pas le créneau. La réservation prend effet
            après confirmation.
          </p>
        )}
        {r.cancellation_allowed && (
          <button
            disabled={busy}
            className="button"
            onClick={() => void cancel(r)}
          >
            Annuler ma demande ou réservation
          </button>
        )}
        <p className="fineprint">Référence : {r.id}</p>
      </article>
    );
  }
  return (
    <>
      <PageTitle eyebrow="Faire de la place" title="Réserver">
        Choisir un espace et soumettre une demande à l’équipe.
      </PageTitle>
      {pendingCommand && (
        <aside className="note">
          <p>
            L’envoi précédent reste à vérifier : {pendingCommand.body.purpose},
            du {pilotTime(pendingCommand.body.starts_at, "UTC")} au{" "}
            {pilotTime(pendingCommand.body.ends_at, "UTC")} (UTC). La répétition
            conserve les conditions déjà validées pour cet envoi.
          </p>
          <button
            type="button"
            className="button"
            disabled={busy}
            onClick={() => void submit(true)}
          >
            Réessayer l’envoi précédent
          </button>
        </aside>
      )}
      {message && (
        <p role="status" className="notice">
          {message}
        </p>
      )}
      <div className="segments">
        <button
          disabled={busy}
          aria-pressed={!mine && !reservation}
          onClick={() => {
            setMine(false);
            onReservation(null);
          }}
        >
          Trouver un espace
        </button>
        <button
          disabled={busy}
          aria-pressed={mine || !!reservation}
          onClick={() => {
            setMine(true);
            onReservation(null);
          }}
        >
          Mes réservations
        </button>
      </div>
      {reservation ? (
        booking(reservation)
      ) : mine ? (
        <>
          <p className="note">
            Vos demandes et réservations de tous les lieux.
          </p>
          {signedIn ? (
            engagements.reservations.length ? (
              engagements.reservations.map(booking)
            ) : (
              <Empty title="Aucune demande pour le moment." />
            )
          ) : (
            <button className="button" onClick={onAuth}>
              Me connecter
            </button>
          )}
        </>
      ) : (
        <>
          <PlaceFilter
            catalog={catalog}
            placeId={placeId}
            onChange={(id) => {
              if (!busy) onPlaceChange(id);
            }}
          />
          <div className="stack">
            {catalog.spaces
              .filter((s) => !placeId || s.place_id === placeId)
              .map((s) => (
                <article className="panel" key={s.id} data-space-id={s.id}>
                  <h2>{s.name}</h2>
                  <p>
                    {catalog.places.find((p) => p.id === s.place_id)?.name} ·{" "}
                    {s.capacity} personnes
                  </p>
                  <p>{s.equipment?.join(" · ")}</p>
                  <p>{s.conditions}</p>
                  <p>
                    {s.commercial_state === "established"
                      ? s.commercial_terms
                      : "Conditions commerciales à préciser avec l’équipe ; aucune gratuité implicite."}
                  </p>
                  <button
                    className="button"
                    disabled={busy || s.booking_mode !== "request"}
                    aria-pressed={s.id === draft.space_id}
                    onClick={() =>
                      onDraft({
                        ...draft,
                        space_id: s.id,
                      })
                    }
                  >
                    {s.id === draft.space_id
                      ? "Espace sélectionné"
                      : "Choisir cet espace"}
                  </button>
                </article>
              ))}
          </div>
          {selected && (
            <form
              className="panel form-stack"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <h2>Votre demande · {selected.name}</h2>
              <p>
                Heures du lieu : {selected.timezone}. Les demandes en attente ne
                bloquent pas les disponibilités.
              </p>
              <label>
                Date
                <input
                  required
                  disabled={busy}
                  type="date"
                  value={draft.date}
                  onChange={(e) =>
                    onDraft({
                      ...draft,
                      date: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Heure de début
                <input
                  required
                  disabled={busy}
                  type="time"
                  value={draft.time}
                  onChange={(e) =>
                    onDraft({
                      ...draft,
                      time: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Durée en minutes
                <input
                  required
                  disabled={busy}
                  type="number"
                  min={selected.min_duration_minutes}
                  max={selected.max_duration_minutes}
                  value={draft.duration}
                  onChange={(e) =>
                    onDraft({
                      ...draft,
                      duration: e.target.value,
                    })
                  }
                />
              </label>
              <label>
                Votre projet
                <textarea
                  required
                  disabled={busy}
                  maxLength={1000}
                  value={draft.purpose}
                  onChange={(e) =>
                    onDraft({
                      ...draft,
                      purpose: e.target.value,
                    })
                  }
                />
              </label>
              <p>{selected.conditions}</p>
              <p>
                {selected.commercial_terms ||
                  "Les conditions commerciales restent à convenir."}
              </p>
              <p>
                Annulation possible jusqu’à{" "}
                {selected.cancellation_notice_minutes} minute(s) avant le début
                après confirmation. Aucun paiement n’est effectué dans l’App.
              </p>
              <p>
                Durée : {selected.min_duration_minutes} à{" "}
                {selected.max_duration_minutes} minutes. Préavis minimum :{" "}
                {selected.min_notice_minutes} minutes.
              </p>
              {!conditionsAvailable && (
                <p role="alert">
                  Les conditions ne peuvent pas être vérifiées avec cette
                  version du service. Rechargez le catalogue avant de déposer
                  une demande.
                </p>
              )}
              <label className="check-label">
                <input
                  type="checkbox"
                  required
                  disabled={
                    busy || conditionsReloadRequired || !conditionsAvailable
                  }
                  checked={conditionsAccepted}
                  onChange={(e) =>
                    setAcceptedConditions(
                      e.target.checked
                        ? {
                            space_id: selected.id,
                            version: selected.conditions_version,
                          }
                        : null,
                    )
                  }
                />
                J’ai consulté les conditions affichées pour cette demande.
              </label>

              <button
                className="button primary"
                disabled={
                  busy ||
                  conditionsReloadRequired ||
                  !conditionsAvailable ||
                  !!pendingCommand
                }
                type="submit"
              >
                {signedIn
                  ? "Envoyer ma demande"
                  : "Me connecter pour envoyer ma demande"}
              </button>
            </form>
          )}
        </>
      )}
    </>
  );
}
